import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription } from 'rxjs'
import { fromJS, List } from 'immutable'
import {
  DeleteDocumentRequest,
  DownloadPropertyDocumentRequest,
  FetchPropertyDocumentsRequest,
  GeneratePropertyDocumentRequest,
  UploadDocumentRequest,
} from '../properties.actions'
import { getPropertyState } from '../properties.selectors'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { getDocumentTypesState } from '@containers/document-types/document-types.selectors'
import { FetchDocumentTypesRequest } from '@containers/document-types/document-types.actions'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import jsonToFormData from 'json-form-data'
import { fileClass } from '@shared/shared.utils'
import { NgxPermissionsService } from 'ngx-permissions'

enum DATA_CYCLE_FILTER {
  Current,
  All,
}
@Component({
  selector: 'app-property-documents',
  templateUrl: './property-documents.component.html',
  styleUrls: ['./property-documents.component.css'],
})
export class PropertyDocumentsComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public propertyId: number
  public licenses: any = fromJS([])
  public documents: any = fromJS([])
  private state$: Subscription
  public isLoading: boolean
  public hasReadonlyPermission: boolean = false
  public isGenerating: boolean
  public licensesColumns = [
    {
      prop: 'referenceId',
      name: 'REFERENCE_ID',
    },
    {
      prop: 'dataCycle',
      name: 'YEAR',
      render: (row: any) => row.get('year'),
    },
    {
      prop: 'propertyType',
      name: 'PROPERTY_TYPE',
    },
    {
      prop: 'fileName',
      name: 'DOCUMENT_NAME',
    },
    {
      prop: 'generatedOn',
      name: 'GENERATED_AT',
      template: 'generatedOn',
    },
    {
      prop: 'download',
      template: 'actionDownload',
    },
  ]
  public radioOptions = [
    {
      label: 'CURRENT_ONLY',
      value: DATA_CYCLE_FILTER.Current,
    },
    {
      label: 'ALL_RECORDS',
      value: DATA_CYCLE_FILTER.All,
    },
  ]

  public dataCycle: DATA_CYCLE_FILTER = DATA_CYCLE_FILTER.Current
  public currentDataCycleId: any
  public settings$: Subscription

  public documentTypes: Map<string, any>
  private documentTypes$: Subscription
  public isUploadModalOpen: boolean
  public uploadForm: FormGroup
  public fileClass = fileClass
  public tabs = []
  public activeTab = 0
  public isReadonly = false

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    public permissionsService: NgxPermissionsService,
  ) {
    super()
  }

  ngOnInit() {
    this.propertyId = parseInt(this.route.parent.snapshot.params.id, 10)
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined

    this.uploadForm = new FormGroup({
      description: new FormControl('', [Validators.required]),
      typeId: new FormControl(),
      file: new FormControl('', [Validators.required]),
    })

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((settings) => {
        this.currentDataCycleId = settings.getIn([
          'data',
          'system',
          'SYSTEM_ACTIVE_DATA_CYCLE',
          'id',
        ])
      })

    this.state$ = this.store
      .pipe(select(getPropertyState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.getIn(['documents', 'isLoading'])
        this.isGenerating = state.getIn(['documents', 'isGenerating'])
        this.licenses = this.filterDocuments(
          state.getIn(['documents', 'data', 'rdns']),
        )

        this.documents = state.getIn(['documents', 'data', 'documents'])

        if (this.propertyId) {
          this.isReadonly =
            state.getIn(['data', 'status', 'name']) === 'ARCHIVED'
        }

        if (this.isDataEntry) {
          this.activeTab = 1
        }
      })

    this.documentTypes$ = this.store
      .pipe(select(getDocumentTypesState))
      .subscribe((state) => {
        this.documentTypes = state.get('data')
      })

    this.store.dispatch<any>(new FetchPropertyDocumentsRequest(this.propertyId))
    this.store.dispatch<any>(new FetchDocumentTypesRequest({}))
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.settings$.unsubscribe()
    this.documentTypes$.unsubscribe()
  }

  public deleteDocument(id: number) {
    this.store.dispatch<any>(new DeleteDocumentRequest(id))
  }

  public dataCycleChange(value) {
    this.dataCycle = value

    this.licenses = this.filterDocuments(
      this.state.getIn(['documents', 'data', 'rdns']),
    )

    // this.documents = this.filterDocuments(
    //   this.state.getIn(['documents', 'data', 'documents']),
    // )
  }

  public filterDocuments(documents: any) {
    return documents.filter((document) =>
      this.dataCycle === DATA_CYCLE_FILTER.Current
        ? document.getIn(['dataCycle', 'id']) === this.currentDataCycleId
        : true,
    )
  }

  public download(document: any) {
    this.store.dispatch(
      new DownloadPropertyDocumentRequest({
        propertyId: this.propertyId,
        dataCycleId: document.getIn(['dataCycle', 'id']),
        propertyType: document.get('propertyType'),
        id: document.get('id'),
      }),
    )
  }

  public generate(document: any) {
    this.store.dispatch(
      new GeneratePropertyDocumentRequest({
        propertyId: this.propertyId,
        propertyType: document.get('propertyType'),
      }),
    )
  }

  public documentChange(event: any) {
    this.uploadForm.get('file').setValue(event.target.files[0])
  }

  public upload() {
    if (this.uploadForm.valid) {
      this.store.dispatch<any>(
        new UploadDocumentRequest(
          this.propertyId,
          jsonToFormData(this.uploadForm.value),
        ),
      )
      this.toggleModal()
      this.uploadForm.reset()
    } else {
      this.uploadForm.markAllAsTouched()
    }
  }

  public getDocumentsByType(typeId: string): List<Map<string, any>> {
    return (this.documents as any).filter(
      (document: Map<string, any>) => document.get('typeId') === typeId,
    )
  }

  public toggleModal() {
    this.isUploadModalOpen = !this.isUploadModalOpen
  }

  public uploadDocument(typeId: number) {
    this.uploadForm.get('typeId').setValue(typeId)
    this.toggleModal()
  }

  public handleTabChange(tab: any) {
    this.activeTab = tab
  }

  get isDataEntry() {
    return this.state.getIn(['data', 'status', 'name']) === 'DATA_ENTRY'
  }
}
